import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import { asKw } from "../../scripts/utils";
import { DeviceClass, isDisabled, isReadOnly } from "../../model/device";

import AlertSummary from "../alerting/alert_summary";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import * as React from "react";
import { StatusIcon } from "../general/status.js";
import { getTypeAlias } from "../../model/device";

import ElectricMeterIcon from "@mui/icons-material/ElectricMeter";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import BatteryStdIcon from "@mui/icons-material/BatteryStd";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export function DeviceCard({ device, handleOpen, hasConfigurationMismatch = false }) {
  const theme = useTheme();
  if (!device) return;

  const isControllable = device.device_class !== "meter";

  return (
    <Box
      onClick={handleOpen}
      width="100%"
      height={110}
      sx={{
        position: "relative",
        borderRadius: 3,
        mb: 1,
        border: (theme) => "1px solid " + theme.palette.background.border,
        padding: 2,
        backgroundColor: "white",
        "&:hover": { backgroundColor: "#EEE" },
        cursor: "pointer",
      }}
    >
      {hasConfigurationMismatch ? (
        <Chip
          size="small"
          icon={<WarningAmberIcon style={{ color: "#d97706" }} />}
          label="Config"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            height: 22,
            fontSize: "0.70rem",
            color: "#b45309",
            borderColor: "#f59e0b",
            backgroundColor: "#fff7ed",
            fontWeight: 500,
            "& .MuiChip-label": { px: 0.8 },
          }}
          variant="outlined"
        />
      ) : null}
      <Stack>
        <Stack sx={{ flexDirection: "row", gap: 0.5, alignItems: "center" }}>
          <Name device={device} />
          <AlertSummary device={device} />
        </Stack>
        <Typography variant="body" sx={{ pb: 1 }}>
          <b style={{ color: theme.palette.primary.main }}>{getTypeAlias(device.device_type)}</b>
          <b style={{ color: "lightgrey" }}> • </b>
          <b style={{ color: theme.palette.secondary.main }}>{device.name}</b>
          {isDisabled(device)
            ? [
                <b style={{ color: "lightgrey" }}> • </b>,
                <b style={{ color: theme.palette.tertiary.main }}> Disabled </b>,
              ]
            : isReadOnly(device)
              ? [
                  <b style={{ color: "lightgrey" }}> • </b>,
                  <b style={{ color: theme.palette.tertiary.main }}> Read Only </b>,
                ]
              : null}
        </Typography>

        <StatusLine status={device.status} isControllable={isControllable} />
      </Stack>
    </Box>
  );
}

function StatusLine({ status, isControllable }) {
  return (
    status && (
      <Stack sx={{ flexDirection: "row", gap: 0.5, alignItems: "center" }}>
        <StatusIcon label="Connected" status={status.connection} />
        <StatusIcon label="Modbus" status={status.modbus} />
        <StatusIcon label="Schematic" status={status.schematic} />
        {isControllable ? <StatusIcon label="Controlled" status={status.control} /> : null}
      </Stack>
    )
  );
}

function OperatingIcon({ device, ...props }) {
  if (isDisabled(device)) {
    return <DoDisturbOnIcon sx={{ ...props.sx }} />;
  } else if (isReadOnly(device)) {
    return <ElectricMeterIcon sx={{ ...props.sx }} />;
  }
  if (device.device_class === DeviceClass.METER) {
    return <ElectricMeterIcon sx={{ ...props.sx }} />;
  } else if (device.device_class === DeviceClass.SOLAR_INVERTER) {
    if (device.curtailment_enabled) {
      return <WbSunnyIcon sx={{ ...props.sx }} />;
    } else {
      return <ElectricMeterIcon sx={{ ...props.sx }} />;
    }
  } else if (device.device_class === DeviceClass.BATTERY) {
    if (!device.enabled) {
      return <DoDisturbOnIcon sx={{ ...props.sx }} />;
    }
    if (device.curtailment_enabled && device.battery_tracking_enabled) {
      return (
        <Stack direction="row">
          <BatteryStdIcon sx={{ ...props.sx }} />
          <WbSunnyIcon sx={{ ...props.sx }} />
        </Stack>
      );
    } else if (device.curtailment_enabled && !device.battery_tracking_enabled) {
      return <WbSunnyIcon sx={{ ...props.sx }} />;
    } else if (!device.curtailment_enabled && device.battery_tracking_enabled) {
      return <BatteryStdIcon sx={{ ...props.sx }} />;
    } else if (!device.curtailment_enabled && !device.battery_tracking_enabled) {
      return <ElectricMeterIcon sx={{ ...props.sx }} />;
    }
  } else {
    return <DoDisturbOnIcon sx={{ ...props.sx }} />;
  }
}

function Name({ device }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.7}>
      <OperatingIcon
        device={device}
        sx={{
          fontSize: 16, // Adjusted size to match text
          color: "inherit", // Matches text color
        }}
      />
      <Typography variant="body2">
        <b>{device.device_class.toUpperCase()}</b>
      </Typography>
    </Stack>
  );
}

export function LiveReadings({ device }) {
  const liveOfDevice = null;
  const meteringOfDevice = null;
  const siteData = null;

  const isGridMeter = device && siteData && siteData.config.grid_meter === device.device_id;

  return device && liveOfDevice && meteringOfDevice ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flexWrap: "wrap",
        alignContent: "flex-start",
        height: 60,
      }}
    >
      {[
        meteringOfDevice.l1 !== undefined ? (
          <LiveReadingsLine key={"power_l1"} name={"Power L1"} value={asKw(meteringOfDevice(device.device_id).l1)} />
        ) : null,
        meteringOfDevice.l2 !== undefined ? (
          <LiveReadingsLine key={"power_l2"} name={"Power L2"} value={asKw(meteringOfDevice(device.device_id).l2)} />
        ) : null,
        meteringOfDevice.l3 !== undefined ? (
          <LiveReadingsLine key={"power_l3"} name={"Power L3"} value={asKw(meteringOfDevice(device.device_id).l3)} />
        ) : null,
        isGridMeter ? (
          <LiveReadingsLine
            key={"grid"}
            name={"Grid"}
            value={
              asKw(
                meteringOfDevice(device.device_id).grid_offtake ? meteringOfDevice(device.device_id).grid_offtake : 0,
              ) - (meteringOfDevice(device.device_id).grid_inject ? meteringOfDevice(device.device_id).grid_inject : 0)
            }
          />
        ) : null,
        meteringOfDevice.setpoint !== undefined ? (
          <LiveReadingsLine
            key={"setpoint"}
            name={"Setpoint"}
            value={asKw(meteringOfDevice(device.device_id).setpoint)}
          />
        ) : null,
        meteringOfDevice.flex !== undefined ? (
          <LiveReadingsLine
            key={"flex_(dc)"}
            name={"Flex (DC)"}
            value={asKw(meteringOfDevice(device.device_id).flex)}
          />
        ) : null,
        meteringOfDevice.production !== undefined ? (
          <LiveReadingsLine
            key={"production"}
            name={"Production"}
            value={asKw(meteringOfDevice(device.device_id).production)}
          />
        ) : null,
        meteringOfDevice.consumption !== undefined ? (
          <LiveReadingsLine
            key={"consumption"}
            name={"Consumption"}
            value={asKw(meteringOfDevice(device.device_id).consumption)}
          />
        ) : null,
        meteringOfDevice.curtailment !== undefined ? (
          <LiveReadingsLine
            key={"curtailment"}
            name={"Curtailment"}
            value={asKw(meteringOfDevice(device.device_id).curtailment)}
          />
        ) : null,
      ]}
    </div>
  ) : null;
}

function LiveReadingsLine({ name, value }) {
  const theme = useTheme();

  return (
    <Typography variant="caption" sx={{ display: "block", width: "200px" }}>
      <b
        style={{
          color: theme.palette.text.primary_bold,
          width: "100px",
          paddingRight: 10,
          display: "inline-block",
          textAlign: "left",
        }}
      >
        {name}
      </b>
      <b style={{ color: "#999", display: "inline-block" }}> {value} kW</b>
    </Typography>
  );
}
